import { useMemo, type ReactNode } from 'react';
import { defineTheme, Theme } from '@astryxdesign/core/theme';
import { themes, defaultThemeSlug } from '../astryx-themes';
import { onAccent } from '../../supabase/functions/_shared/domain/business-schema.ts';

const cache = new Map<string, ReturnType<typeof defineTheme>>();

/**
 * One accent from the tenant configuration on top of the built neutral
 * theme. Dark, calm surfaces; on-accent text is picked for contrast.
 */
export function tenantTheme(accent: string) {
  const hex = /^#[0-9a-fA-F]{6}$/.test(accent) ? accent.toUpperCase() : '#4690FF';
  const cached = cache.get(hex);
  if (cached) return cached;
  const t = defineTheme({
    name: `tenant-${hex.slice(1).toLowerCase()}`,
    extends: themes[defaultThemeSlug],
    tokens: {
      '--color-accent': [hex, hex],
      '--color-on-accent': [onAccent(hex), onAccent(hex)],
      '--color-text-accent': [hex, hex],
      '--color-icon-accent': [hex, hex],
      '--color-accent-muted': [`${hex}22`, `${hex}2E`],
      '--color-background-body': ['#F2F3F5', '#050506'],
      '--color-background-surface': ['#FFFFFF', '#121214'],
      '--color-background-card': ['#FFFFFF', '#0D0D0F'],
      '--color-background-popover': ['#FFFFFF', '#151518'],
      '--color-background-muted': ['#ECEDEF', '#1B1B1F'],
      '--color-border': ['#E1E2E5', '#232327'],
      '--focus-outline-color': hex,
    },
  });
  cache.set(hex, t);
  return t;
}

export function TenantTheme({ accent, children }: { accent: string; children: ReactNode }) {
  const theme = useMemo(() => tenantTheme(accent), [accent]);
  return (
    <Theme theme={theme} mode="dark">
      {children}
    </Theme>
  );
}
